import type { PayexThunkAction } from 'store/configureStore';

import { trackPaymentProof } from 'actions';
import type { HttpError } from 'constants/index';
import {
  DELETE_PROOF_FILE_REQUEST,
  DELETE_PROOF_FILE_SUCCESS,
  FETCH_PROOFS_FAILURE,
  FETCH_PROOFS_REQUEST,
  FETCH_PROOFS_SUCCESS,
  FETCH_PROOFS_SUCCESS_WITHOUT_RESULTS,
  HTTPStatuses,
  UPLOAD_PROOF_FAILURE,
  UPLOAD_PROOF_REQUEST,
  UPLOAD_PROOF_SUCCESS,
  USE_PAYEX_PAYMENTS_CONTROLLER,
} from 'constants/index';
import type { RootState } from 'reducers/types';
import { getFeature, getPayment, getPaymentId, isFetching } from 'selectors';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { isHttpErrorWithStatus } from 'utils/errors/errors';

import { normalizeProofs } from 'services/payments/payments';

const shouldUsePayexService = (state: RootState) =>
  getFeature(state, USE_PAYEX_PAYMENTS_CONTROLLER);

export const fetchProofs =
  (): PayexThunkAction<Promise<{ type: string } | undefined>> =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const isFetchingProofs = isFetching(state, 'proofs');
    const id = getPaymentId(state);
    const service = shouldUsePayexService(state) ? payex : apiV3;

    if (isFetchingProofs) {
      return;
    }

    dispatch({ type: FETCH_PROOFS_REQUEST });

    const { token } = getPayment(state);
    try {
      const response = await service.payments.fetchProofs({ id, token });

      return dispatch({
        type: FETCH_PROOFS_SUCCESS,
        payload: normalizeProofs(response),
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const { status } = error as HttpError;

        if (status === HTTPStatuses.NOT_FOUND) {
          return dispatch({
            type: FETCH_PROOFS_SUCCESS_WITHOUT_RESULTS,
          });
        }
      }

      errorNotifier.notify(error);

      return dispatch({ type: FETCH_PROOFS_FAILURE });
    }
  };

export const generateProofUrl =
  (filename: string): PayexThunkAction<Promise<string | undefined>> =>
  async (_dispatch, getState, { apiV3, payex }) => {
    const state = getState();

    const { token } = getPayment(state);
    const id = getPaymentId(state);
    const service = shouldUsePayexService(state) ? payex : apiV3;

    try {
      const response = await service.payments.generateProofUrl({
        id,
        token,
        filename,
      });

      return response.url;
    } catch (error) {
      errorNotifier.notify(error);
    }
  };

export const uploadProof =
  (file: File, url: string): PayexThunkAction =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const isFetchingProofs = isFetching(state, 'proofs');
    const service = shouldUsePayexService(state) ? payex : apiV3;

    if (isFetchingProofs) {
      return;
    }

    dispatch({ type: UPLOAD_PROOF_REQUEST });

    let succeed = true;

    try {
      await service.payments.uploadFileToUrl({ file, url });

      dispatch({
        type: UPLOAD_PROOF_SUCCESS,
      });

      return dispatch(fetchProofs());
    } catch (error) {
      const { name } = file;
      if (isHttpErrorWithStatus(error)) {
        const _error = error as HttpError;
        const { status = '' } = _error;

        if (
          status !== HTTPStatuses.NOT_FOUND &&
          status !== HTTPStatuses.BAD_REQUEST
        ) {
          _error.message = `${UPLOAD_PROOF_FAILURE} ${_error.message}`;
          errorNotifier.notifyWithFingerprint(_error, [
            UPLOAD_PROOF_FAILURE,
            `${status}`,
          ]);
        }
      }

      succeed = false;

      return dispatch({
        type: UPLOAD_PROOF_FAILURE,
        payload: { name },
      });
    } finally {
      dispatch(trackPaymentProof(succeed));
    }
  };

export const deleteProofFile =
  (
    url: string,
    fileId: string,
    proofId: string,
  ): PayexThunkAction<
    Promise<{ type: string; payload: unknown } | undefined>
  > =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const isFetchingProofs = isFetching(state, 'proofs');
    const service = shouldUsePayexService(state) ? payex : apiV3;

    if (isFetchingProofs) {
      return;
    }

    dispatch({ type: DELETE_PROOF_FILE_REQUEST });

    try {
      await service.payments.deleteFile({ url });

      return dispatch({
        type: DELETE_PROOF_FILE_SUCCESS,
        payload: { fileId, proofId },
      });
    } catch (error) {
      errorNotifier.notify(error);
    }
  };
